import React, { useMemo } from 'react';
import { Trash2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import type { PayrollVisitItem } from '../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { getVisitTypeDisplayName } from '../../hooks/useVisitTypeOptions';
import { VISIT_TYPE_LABELS } from '../../constants/visitTypeLabels';
import { employeeTypeLabel } from '../../constants/employeeTypes';

type PayrollGroupBy = 'clinician' | 'visit_date';

const STATUS_LABELS: Record<string, string> = {
  submitted_with_signature: 'Submitted w/ Signature',
  completed: 'Completed',
  exported: 'Exported',
};

function statusLabel(status: string): string {
  return (
    STATUS_LABELS[status] ??
    status
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  );
}

type PayrollVisitsTableProps = {
  visits: PayrollVisitItem[];
  /** Manager groups by clinician; the payroll-detail screen by visit date. */
  groupBy: PayrollGroupBy;
  /** Extra Clinic column (payroll-detail screen). */
  showClinicColumn?: boolean;
  /** Per-row Clinician column (payroll-detail screen, where rows group by date). */
  showClinicianColumn?: boolean;
  /** Visit status column (Payroll Manager). */
  showStatusColumn?: boolean;
  /** Selection controls (Payroll Manager only). */
  selectedIds?: Set<string>;
  onToggleSelect?: (visitId: string) => void;
  onToggleSelectAll?: () => void;
  /** Per-row remove action (payroll-detail screen). */
  onRemoveVisit?: (visit: PayrollVisitItem) => void;
};

/** Patient, Date of Visit, Visit Name, Visit Rate, Mileage, Surcharge, Insurance
 *  Provider — the columns every payroll table renders. Keep in sync with the
 *  header/body below so the group-header colSpan stays correct. */
const ALWAYS_ON_COLUMN_COUNT = 7;

type VisitGroup = {
  key: string;
  label: string;
  visits: PayrollVisitItem[];
};

function formatMoney(value: string | number): string {
  const amount = Number(value);
  return `$${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
}

/** Group consecutive rows; server ordering keeps groups contiguous. */
function buildGroups(
  visits: PayrollVisitItem[],
  groupBy: PayrollGroupBy
): VisitGroup[] {
  const groups: VisitGroup[] = [];
  for (const visit of visits) {
    const key =
      groupBy === 'clinician'
        ? (visit.clinician_user_id ?? 'unassigned')
        : visit.visit_date;
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.visits.push(visit);
      continue;
    }
    const label =
      groupBy === 'clinician'
        ? (visit.clinician_name ?? 'Unassigned')
        : formatDateToMMDDYYYY(visit.visit_date);
    groups.push({ key, label, visits: [visit] });
  }
  return groups;
}

export const PayrollVisitsTable: React.FC<PayrollVisitsTableProps> = ({
  visits,
  groupBy,
  showClinicColumn = false,
  showClinicianColumn = false,
  showStatusColumn = false,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onRemoveVisit,
}) => {
  const selectable = !!selectedIds && !!onToggleSelect;
  const groups = useMemo(() => buildGroups(visits, groupBy), [visits, groupBy]);
  const columnCount =
    ALWAYS_ON_COLUMN_COUNT +
    (selectable ? 1 : 0) +
    (showClinicColumn ? 1 : 0) +
    (showClinicianColumn ? 1 : 0) +
    (showStatusColumn ? 1 : 0) +
    (onRemoveVisit ? 1 : 0);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {selectable && (
            <TableHead className='w-10'>
              <Checkbox
                checked={
                  visits.length > 0 && selectedIds.size === visits.length
                }
                onCheckedChange={onToggleSelectAll}
              />
            </TableHead>
          )}
          <TableHead>Patient</TableHead>
          {showClinicianColumn && <TableHead>Clinician</TableHead>}
          <TableHead>Date of Visit</TableHead>
          <TableHead>Visit Name</TableHead>
          <TableHead className='text-right'>Visit Rate</TableHead>
          <TableHead className='text-right'>Mileage</TableHead>
          <TableHead className='text-right'>Surcharge</TableHead>
          {showStatusColumn && <TableHead>Status</TableHead>}
          {showClinicColumn && <TableHead>Clinic</TableHead>}
          <TableHead>Insurance Provider</TableHead>
          {onRemoveVisit && (
            <TableHead className='w-20 text-center'>Remove</TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {groups.map(group => (
          <React.Fragment key={group.key}>
            <TableRow className='bg-muted/60 hover:bg-muted/60'>
              <TableCell
                colSpan={columnCount}
                className='py-1.5 text-sm font-semibold'
              >
                {group.label}
                {groupBy === 'clinician' &&
                  group.visits[0] &&
                  employeeTypeLabel(
                    group.visits[0].employee_type,
                    group.visits[0].employee_type_other
                  ) && (
                    <span className='ml-2 font-normal text-muted-foreground'>
                      {employeeTypeLabel(
                        group.visits[0].employee_type,
                        group.visits[0].employee_type_other
                      )}
                    </span>
                  )}
              </TableCell>
            </TableRow>
            {group.visits.map(visit => (
              <TableRow key={visit.visit_id}>
                {selectable && (
                  <TableCell className='w-10'>
                    <Checkbox
                      checked={selectedIds.has(visit.visit_id)}
                      onCheckedChange={() => onToggleSelect(visit.visit_id)}
                    />
                  </TableCell>
                )}
                <TableCell className='text-sm font-medium'>
                  {visit.patient_name}
                </TableCell>
                {showClinicianColumn && (
                  <TableCell className='text-sm'>
                    {visit.clinician_name ?? 'Unassigned'}
                  </TableCell>
                )}
                <TableCell className='text-sm'>
                  {formatDateToMMDDYYYY(visit.visit_date)}
                </TableCell>
                <TableCell className='text-sm'>
                  {VISIT_TYPE_LABELS[visit.visit_type] ??
                    getVisitTypeDisplayName(visit.visit_type)}
                </TableCell>
                <TableCell className='text-right text-sm'>
                  {formatMoney(visit.visit_rate ?? 0)}
                </TableCell>
                <TableCell className='text-right text-sm'>
                  {visit.mileage ?? 0}
                </TableCell>
                <TableCell className='text-right text-sm'>
                  {formatMoney(visit.surcharge ?? 0)}
                </TableCell>
                {showStatusColumn && (
                  <TableCell className='text-sm'>
                    {statusLabel(visit.status)}
                  </TableCell>
                )}
                {showClinicColumn && (
                  <TableCell className='text-sm'>
                    {visit.clinic_name ?? '—'}
                  </TableCell>
                )}
                <TableCell className='text-sm'>
                  {visit.insurance_name ?? '—'}
                </TableCell>
                {onRemoveVisit && (
                  <TableCell className='w-20 text-center'>
                    <Button
                      size='sm'
                      variant='ghost'
                      aria-label='Remove visit from payroll'
                      onClick={() => onRemoveVisit(visit)}
                    >
                      <Trash2 className='h-4 w-4 text-destructive' />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );
};
